import React, { Component } from "react";
import { DateRangePicker, Select, toFixed, format_date, GET, loading, Navbar, XLSX, saveAs } from "../../components/CustomComponent.js";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import GatewayLogo from "../../components/GatewayLogo.js";

// GMV Analytics (เดิม GMV Monitor, เมนูอยู่ใต้ Transaction Analytics): dashboard ยอด GMV (ยอดโอนเข้าที่สำเร็จ)
//   - กราฟ GMV ต่อวัน/เดือน แยกสีตาม gateway + ตัวกรอง company / gateway / broker / ช่วงวันที่
//   - broker ไหนส่งเข้า gateway เท่าไหร่ ดูที่การ์ดเพดานด้านล่าง (การ์ด By Gateway / By Company ผู้ใช้สั่งเอาออก 2026-10-21) และใน Export Excel
//   - เพดาน GMV วันนี้/เดือนนี้ของแต่ละ company (ส่วนเดิมของ GMV Monitor) + broker ที่ใช้อยู่ในแต่ละ gateway
// ข้อมูลกราฟ: GET v1/admin/gmv-analytics · เพดาน: GET v1/admin/gateway-companies (master ของ Center Config › Config Company)

const REFRESH_MS = 30000;

const PERIODS = [
  { key: "today", label: "Today", usage: "usage_today", limit: "limit_gmv_per_day", broker: "today_amount" },
  { key: "month", label: "This month", usage: "usage_month", limit: "limit_gmv_per_month", broker: "month_amount" },
];
const GROUP_OPTIONS = [
  { key: "day", label: "Daily", unit: "day" },
  { key: "month", label: "Monthly", unit: "month" },
];
// สีประจำ gateway บนกราฟ (เรียงตามยอด ตัวที่ยอดมากสุดได้สีแรก)
const GATEWAY_COLORS = ["13, 148, 136", "99, 102, 241", "217, 119, 6", "236, 72, 153", "14, 165, 233", "132, 204, 22", "168, 85, 247", "239, 68, 68", "100, 116, 139"];
const PICK_FILTERS = ["filter_company", "filter_gateway", "filter_broker"];
const EMPTY_PICKS = Object.fromEntries(PICK_FILTERS.map((k) => [k, ""]));

const limitOf = (v) => (v === null || v === undefined || v === "" || Number(v) <= 0 ? null : Number(v));
const fmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money = (v) => toFixed(v, 2);
const num = (v) => Number(v) || 0;
const toneOf = (p) => (p === null ? "none" : p >= 100 ? "full" : p >= 80 ? "warn" : "ok");

function stat(row, period) {
  const used = Number(row[period.usage]?.total_amount || 0);
  const limit = limitOf(row[period.limit]);
  const percent = limit ? (used / limit) * 100 : null;
  return { used, limit, percent, left: limit ? Math.max(0, limit - used) : null, tone: toneOf(percent) };
}

// รวมแถว master ตามชื่อ company
function groupByCompany(rows, period) {
  const map = new Map();
  rows.forEach((r) => {
    if (!map.has(r.company_name)) map.set(r.company_name, []);
    map.get(r.company_name).push(r);
  });
  return [...map.entries()]
    .map(([name, list]) => {
      const stats = list.map((r) => stat(r, period));
      const worst = stats.some((s) => s.tone === "full") ? "full" : stats.some((s) => s.tone === "warn") ? "warn" : stats.every((s) => s.tone === "none") ? "none" : "ok";
      const brokers = new Set();
      list.forEach((r) => (r.brokers || []).forEach((b) => brokers.add(b.uid)));
      const top = Math.max(...stats.map((s) => s.percent ?? -1));
      return {
        name,
        rows: [...list].sort((a, b) => (stat(b, period).percent ?? -1) - (stat(a, period).percent ?? -1)),
        worst,
        top,
        used: stats.reduce((a, s) => a + s.used, 0),
        broker_count: brokers.size,
      };
    })
    .sort((a, b) => b.top - a.top || a.name.localeCompare(b.name));
}

const STATUS = { none: "No limit", ok: "Healthy", warn: "Near limit", full: "Limit reached" };

export default class GmvAnalytics extends Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      // เพดาน GMV (ส่วนเดิม)
      limit_loaded: false,
      limit_rows: [],
      period: "today",
      auto_refresh: true,
      updated_at: null,
      // dashboard
      filter_start_date: new Date(now.getFullYear(), now.getMonth(), 1),
      filter_end_date: now,
      filter_currency: "THB",
      filter_group: "day",
      filter_company: "",
      filter_gateway: "",
      filter_broker: "",
      options: { gateways: [], companies: [], brokers: [] },
      report: null,
      loaded: false,
    };
    this.request_id = 0;
  }

  componentDidMount() {
    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
    this.GetOptions();
    this.Get();
    this.GetLimits(false);
    this.timer = setInterval(() => {
      if (this.state.auto_refresh && document.visibilityState === "visible") this.GetLimits(false);
    }, REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  GetLimits = async (showLoading) => {
    if (showLoading) loading(true);
    const result = await GET("v1/admin/gateway-companies", null);
    if (result?.status) this.setState({ limit_rows: result.data || [], updated_at: new Date() });
    this.setState({ limit_loaded: true });
    if (showLoading) loading(false);
  };

  GetOptions = async () => {
    const result = await GET("v1/admin/gmv-analytics/options", null);
    if (result?.status) {
      this.setState({
        options: {
          gateways: (result.data?.gateways || []).map((g) => ({
            ...g,
            front: (
              <span className="me-2">
                <GatewayLogo name={g.value} size={18} />
              </span>
            ),
          })),
          companies: (result.data?.companies || []).map((c) => ({ ...c, gateways: c.gateways || [], broker_uids: c.broker_uids || [] })),
          brokers: result.data?.brokers || [],
        },
      });
    }
  };

  Get = async () => {
    const { filter_start_date, filter_end_date, filter_currency, filter_group, filter_company, filter_gateway, filter_broker } = this.state;
    if (!filter_start_date || !filter_end_date) return;
    const request_id = ++this.request_id;
    loading(true);
    const result = await GET("v1/admin/gmv-analytics", {
      currency: filter_currency,
      group_by: filter_group,
      start_date: format_date(filter_start_date, "yyyy-mm-dd", "en"),
      end_date: format_date(filter_end_date, "yyyy-mm-dd", "en"),
      ...(filter_company ? { company_id: filter_company } : {}),
      ...(filter_gateway ? { gateway: filter_gateway } : {}),
      ...(filter_broker ? { broker_id: filter_broker } : {}),
    });
    // ผลของ request เก่าที่มาช้ากว่าตัวล่าสุดไม่เอามาทับ
    if (request_id !== this.request_id) return;
    this.setState({ report: result?.status ? result.data : null, loaded: true });
    loading(false);
  };

  SetFilter = (patch) => this.setState(patch, () => this.Get());

  // company ผูกกับ gateway และ broker: เปลี่ยนสองตัวนั้นแล้ว company ที่เลือกอยู่ไม่เข้าเงื่อนไขก็ล้างทิ้ง
  SetScopedFilter = (patch) => {
    const next = { ...this.state, ...patch };
    const company = this.state.options.companies.find((c) => c.value === next.filter_company);
    const keep =
      !company ||
      ((!next.filter_gateway || company.gateways.includes(next.filter_gateway)) && (!next.filter_broker || company.broker_uids.includes(next.filter_broker)));
    this.SetFilter(keep ? patch : { ...patch, filter_company: "" });
  };

  // ปุ่มลัดช่วงวันที่
  SetRange = (key) => {
    const now = new Date();
    const start =
      key === "today"
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
        : key === "7d"
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)
          : key === "30d"
            ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29)
            : key === "year"
              ? new Date(now.getFullYear(), 0, 1)
              : new Date(now.getFullYear(), now.getMonth(), 1);
    this.SetFilter({ filter_start_date: start, filter_end_date: now, ...(key === "year" ? { filter_group: "month" } : {}) });
  };

  ExportExcel = () => {
    const { report, filter_currency } = this.state;
    if (!report) return;
    const workbook = XLSX.utils.book_new();
    const codes = (report.gateways || []).map((g) => g.key);
    const sheet = (rows) => XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(
      workbook,
      sheet([
        ["Period", "Txns", "GMV", "Avg / Txn", ...(report.gateways || []).map((g) => g.name)],
        ...(report.series || []).map((s) => [s.period, s.orders, s.amount, s.avg_amount, ...codes.map((c) => s.gateways?.[c] || 0)]),
        ["TOTAL", report.summary.orders, report.summary.amount, report.summary.avg_amount, ...(report.gateways || []).map((g) => g.amount)],
      ]),
      "Periods"
    );
    XLSX.utils.book_append_sheet(
      workbook,
      sheet([
        ["Gateway", "Broker", "Txns", "GMV", "Share of total %"],
        ...(report.gateways || []).flatMap((g) => [[g.name, "ALL", g.orders, g.amount, g.share], ...g.brokers.map((b) => [g.name, b.name, b.orders, b.amount, b.share])]),
      ]),
      "Gateway x Broker"
    );
    XLSX.utils.book_append_sheet(
      workbook,
      sheet([
        ["Company", "Gateway", "Broker", "Txns", "GMV"],
        ...(report.companies || []).flatMap((c) => [
          [c.name, "ALL", "ALL", c.orders, c.amount],
          ...c.gateways.flatMap((g) => [[c.name, g.name, "ALL", g.orders, g.amount], ...g.brokers.map((b) => [c.name, g.name, b.name, b.orders, b.amount])]),
        ]),
      ]),
      "Companies"
    );
    XLSX.utils.book_append_sheet(workbook, sheet([["Broker", "Txns", "GMV", "Avg / Txn", "Share %"], ...(report.brokers || []).map((b) => [b.name, b.orders, b.amount, b.avg_amount, b.share])]), "Brokers");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), `gmv-analytics-${report.start_date}_${report.end_date}-${report.group_by}-${filter_currency}.xlsx`);
  };

  // ===== เพดาน GMV ต่อ company (ส่วนเดิม) + broker ที่ใช้อยู่ในแต่ละ gateway =====
  renderLimitRow(r, period) {
    const s = stat(r, period);
    const brokers = [...(r.brokers || [])].sort((a, b) => num(b[period.broker]) - num(a[period.broker]));
    return (
      <div className={`gm-row ${s.tone}`} key={r.uid}>
        <div className="d-flex justify-content-between align-items-baseline gap-2">
          <div className="gm-row-name d-flex align-items-center gap-2 min-w-0">
            <GatewayLogo name={r.gateway_name} size={22} />
            <span className="text-truncate">
              {r.gateway_name}
              <span className="gm-row-sub"> · {r.business_type}</span>
            </span>
          </div>
          <div className="gm-row-value">
            {s.percent === null ? <span className="gm-row-pct none">No limit</span> : <span className={`gm-row-pct ${s.tone}`}>{s.percent >= 100 ? Math.round(s.percent) : s.percent.toFixed(1)}%</span>}
          </div>
        </div>
        <div className="gm-row-bar">
          <span className={s.tone} style={{ width: `${s.percent === null ? 0 : Math.min(100, s.percent)}%` }} />
        </div>
        <div className="gm-row-meta">
          <span className={`gm-chip used ${s.tone}`}>
            Used <b>{fmt(s.used)}</b> / {s.limit ? fmt(s.limit) : "Unlimited"}
          </span>
          {s.left !== null && (
            <span className="gm-chip">
              Left <b>{fmt(s.left)}</b>
            </span>
          )}
        </div>
        <div className="gm-brokers">
          {brokers.length === 0 && <div className="gm-broker-none">No broker uses this gateway</div>}
          {brokers.map((b) => {
            const amount = num(b[period.broker]);
            return (
              <div className={`gm-broker ${amount ? "" : "zero"}`} key={b.uid || b.name}>
                <span className={`gm-broker-dot ${b.is_active ? "on" : ""}`} title={b.is_active ? "Active" : "Inactive"} />
                <span className="gm-broker-name text-truncate">{b.name}</span>
                <span className="gm-broker-amt">{fmt(amount)}</span>
                <span className="gm-broker-share">{s.used ? `${((amount / s.used) * 100).toFixed(0)}%` : "–"}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderLimitCompany(c, period) {
    const gateways = new Set(c.rows.map((r) => r.gateway_name)).size;
    return (
      <div className="col-12 col-md-6 col-xxl-4" key={c.name}>
        <div className={`ar-table-card h-100 gm-card2 ${c.worst}`}>
          <div className="ar-table-head">
            <div className="min-w-0">
              <div className="ar-table-title text-truncate">{c.name}</div>
              <div className="ar-table-caption">
                {gateways} gateway{gateways === 1 ? "" : "s"} · {c.broker_count} broker{c.broker_count === 1 ? "" : "s"} · used {fmt(c.used)} THB {period.label.toLowerCase()}
              </div>
            </div>
            <span className={`gm-status ${c.worst}`}>{STATUS[c.worst]}</span>
          </div>
          <div className="gm-rows">{c.rows.map((r) => this.renderLimitRow(r, period))}</div>
        </div>
      </div>
    );
  }

  render() {
    const { limit_loaded, limit_rows, period: periodKey, auto_refresh, updated_at } = this.state;
    const { filter_start_date, filter_end_date, filter_currency, filter_group, filter_company, filter_gateway, filter_broker, options, report, loaded } = this.state;

    const period = PERIODS.find((p) => p.key === periodKey);
    const companyOption = options.companies.find((c) => c.value === filter_company);
    // การ์ดเพดานใช้ตัวกรอง company / gateway เดียวกับ dashboard
    const limitRows = limit_rows.filter(
      (r) => (!filter_company || String(r.company_name || "").trim().toLowerCase() === filter_company) && (!filter_gateway || String(r.gateway_name || "").trim().toLowerCase() === filter_gateway)
    );
    const groups = groupByCompany(limitRows, period);
    const attention = groups.filter((g) => g.worst === "warn" || g.worst === "full").length;

    const currencySymbol = filter_currency === "THB" ? "฿" : filter_currency === "LAK" ? "₭" : "";
    const summary = report?.summary;
    const series = report?.series || [];
    const gatewayList = report?.gateways || [];
    const group = GROUP_OPTIONS.find((g) => g.key === (report?.group_by || filter_group)) || GROUP_OPTIONS[0];
    const rangeLabel =
      filter_start_date && filter_end_date ? `${format_date(filter_start_date, "dd/mm/yyyy", "en")} – ${format_date(filter_end_date, "dd/mm/yyyy", "en")}` : "Select end date";
    const gatewayOptions = options.gateways.filter((g) => !g.currency || g.currency === filter_currency);
    const companyOptions = options.companies.filter((c) => (!filter_gateway || c.gateways.includes(filter_gateway)) && (!filter_broker || c.broker_uids.includes(filter_broker)));
    const activePicks = PICK_FILTERS.filter((k) => this.state[k]).length;
    const hasData = num(summary?.orders) > 0;
    const topGateway = gatewayList[0];

    const kpis = summary
      ? [
          { label: "Total GMV", value: money(summary.amount), sub: `${rangeLabel}`, accent: "#d4af37", icon: "", highlight: true },
          { label: "Deposit Transactions", value: fmt(summary.orders), sub: `Avg ${currencySymbol}${money(summary.avg_amount)} / txn`, accent: "#14b8a6", icon: "", plain: true },
          {
            label: `Avg GMV / ${group.unit}`,
            value: money(summary.per_period),
            sub: summary.peak ? `Peak ${summary.peak.period} · ${currencySymbol}${money(summary.peak.amount)}` : `over ${fmt(report.periods)} ${group.unit}s`,
            accent: "#6366f1",
            icon: "",
          },
          {
            label: "Top Gateway",
            value: topGateway ? topGateway.name : "—",
            sub: topGateway ? `${currencySymbol}${money(topGateway.amount)} · ${toFixed(topGateway.share, 1)}% of GMV` : "No transactions",
            accent: "#f59e0b",
            icon: "",
            plain: true,
            text: true,
          },
        ]
      : [];

    const chartData = {
      labels: series.map((s) => s.period),
      datasets: gatewayList.map((g, i) => {
        const color = GATEWAY_COLORS[i % GATEWAY_COLORS.length];
        return {
          id: g.key,
          label: g.name,
          data: series.map((s) => num(s.gateways?.[g.key])),
          backgroundColor: `rgba(${color}, 0.88)`,
          hoverBackgroundColor: `rgba(${color}, 1)`,
          borderRadius: 3,
          maxBarThickness: 28,
          stack: "gmv",
        };
      }),
    };
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: {
          filter: (item) => item.parsed.y > 0,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${currencySymbol}${money(ctx.parsed.y)}`,
            footer: (items) => `Total: ${currencySymbol}${money(items.reduce((a, it) => a + it.parsed.y, 0))}`,
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 16 } },
        y: { stacked: true, beginAtZero: true, grid: { color: "rgba(148,163,184,0.15)" }, ticks: { callback: (v) => Number(v).toLocaleString() } },
      },
    };


    return (
      <div className="w-100 min-vh-100 premium-report pr-scope pu-scope">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* ===== Hero + filters ===== */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Reports</div>
                <div className="ar-title">GMV Analytics</div>
                <div className="ar-subtitle">Successful deposit volume by day, gateway, company and broker · GMV limit usage</div>
              </div>
              <div className="ar-hero-actions">
                <button className="ar-btn-ghost" onClick={this.ExportExcel} disabled={!report}>
                  <span className="icon me-2">{""}</span>Export Excel
                </button>
                <div className="text-end ms-2">
                  <div className="ar-year">
                    {summary ? `${currencySymbol}${fmt(summary.amount)}` : "—"}
                    <span className="ar-year-small"> GMV</span>
                  </div>
                  <div className="ar-year-caption">
                    <span className="ar-caption-chip ms-2">{rangeLabel}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="ar-toolbar ar-filter ta-filter mt-4">
              <div className="ar-filter-head ta-filter-head">
                <div className="ta-filter-head-right">
                  <div className="ta-filter-group">
                    <span className="ar-filter-label">Currency</span>
                    <div className="d-flex align-items-center gap-2">
                      {global.currency?.map((item) => (
                        <button
                          key={item.currency_code}
                          className={`ar-pill ${item.currency_code === filter_currency ? "active" : ""}`}
                          onClick={() => this.SetFilter({ filter_currency: item.currency_code, filter_gateway: "", filter_company: "" })}
                        >
                          {item.image}
                          <span className="ms-1">{item.currency_code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ta-filter-group">
                    <span className="ar-filter-label">Group by</span>
                    <div className="ar-seg ta-seg-hero">
                      {GROUP_OPTIONS.map((o) => (
                        <button key={o.key} className={`ar-seg-btn ${filter_group === o.key ? "active" : ""}`} onClick={() => this.SetFilter({ filter_group: o.key })}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ta-filter-group ta-date-field gm-date-field">
                    <span className="ar-filter-label">Date Range</span>
                    <div className="ta-date-row">
                      <DateRangePicker
                        className="form-control"
                        value={{ start_date: filter_start_date, end_date: filter_end_date }}
                        onChange={(e) => this.setState({ filter_start_date: e.start_date, filter_end_date: e.end_date }, () => this.Get())}
                      />
                      <div className="gm-presets">
                        {[
                          ["today", "Today"],
                          ["7d", "7D"],
                          ["30d", "30D"],
                          ["month", "This month"],
                          ["year", "This year"],
                        ].map(([k, label]) => (
                          <button key={k} className="ar-pill ta-pill-text" onClick={() => this.SetRange(k)}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ar-filter-row">
                <div className="ar-filter-field">
                  <span className="ar-filter-label">Company</span>
                  <Select className="form-control" placeholder="All companies" options={companyOptions} value={filter_company} onChange={(v) => this.SetFilter({ filter_company: v })} />
                </div>
                <div className="ar-filter-field">
                  <span className="ar-filter-label">Gateway</span>
                  <Select className="form-control" placeholder="All gateways" options={gatewayOptions} value={filter_gateway} onChange={(v) => this.SetScopedFilter({ filter_gateway: v })} />
                </div>
                {options.brokers.length > 0 && (
                  <div className="ar-filter-field">
                    <span className="ar-filter-label">Broker</span>
                    <Select className="form-control" placeholder="All brokers" options={options.brokers} value={filter_broker} onChange={(v) => this.SetScopedFilter({ filter_broker: v })} />
                  </div>
                )}
                {activePicks > 0 && (
                  <div className="ar-toolbar-actions">
                    <button className="ar-btn-ghost" onClick={() => this.SetFilter({ ...EMPTY_PICKS })}>
                      <span className="icon me-2">{""}</span>Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== KPI ===== */}
          {kpis.length > 0 && (
            <div className="row g-3 mb-4">
              {kpis.map((k) => (
                <div className="col-12 col-sm-6 col-xl-3" key={k.label}>
                  <div className={`ar-kpi ${k.highlight ? "ar-kpi-highlight" : ""}`} style={{ "--ar-accent": k.accent }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="ar-kpi-label">{k.label}</div>
                      <div className="ar-kpi-icon">
                        <span className="icon">{k.icon}</span>
                      </div>
                    </div>
                    <div className={`ar-kpi-value ${k.text ? "gm-kpi-text text-truncate" : ""}`}>
                      {!k.plain && <span className="ar-kpi-currency">{currencySymbol}</span>}
                      {k.value}
                    </div>
                    <div className="ar-kpi-sub">{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== GMV trend by gateway ===== */}
          <div className="ar-table-card mb-4">
            <div className="ar-table-head flex-wrap gap-2">
              <div>
                <div className="ar-table-title">{group.label} GMV</div>
                <div className="ar-table-caption">
                  Successful deposit amount per {group.unit} · stacked by gateway · {filter_currency}
                  {companyOption && <span className="ar-caption-chip ms-2">{companyOption.label}</span>}
                  {report && report.group_by !== report.group_by_requested && (
                    <span className="ar-chip-empty ms-2">
                      range too long for {report.group_by_requested} view · showing by {report.group_by}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {hasData ? (
              <div className="ar-chart">
                <Bar datasetIdKey="id" options={chartOptions} data={chartData} />
              </div>
            ) : (
              loaded && (
                <div className="ar-empty py-5 text-center">
                  <span className="icon d-block mb-2">{""}</span>
                  No GMV for {rangeLabel} · {filter_currency}
                </div>
              )
            )}
          </div>

          {/* ===== GMV limit usage (ส่วนเดิมของ GMV Monitor) ===== */}
          <div className="gm-section-head gm-section-head-actions mb-2">
            <div>
              <div className="gm-section-title">GMV Limit Usage</div>
              <div className="gm-section-caption">
                {groups.length} compan{groups.length === 1 ? "y" : "ies"} · limits are shared by every broker · broker amounts are {period.label.toLowerCase()}
                {attention > 0 && <span className="gm-attn-dark"> · {attention} need attention</span>}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="gm-seg gm-seg-light">
                {PERIODS.map((p) => (
                  <button type="button" key={p.key} className={p.key === periodKey ? "active" : ""} onClick={() => this.setState({ period: p.key })}>
                    {p.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={`gm-live-btn ${auto_refresh ? "gm-live" : ""}`}
                onClick={() => this.setState({ auto_refresh: !auto_refresh })}
                title={updated_at ? `Updated ${updated_at.toLocaleTimeString()} · refreshes every 30 seconds` : ""}
              >
                <span className="gm-dot" />
                {auto_refresh ? "Live" : "Paused"}
              </button>
              <button type="button" className="gm-live-btn" onClick={() => this.GetLimits(true)}>
                <span className="icon me-1">{""}</span>Refresh
              </button>
            </div>
          </div>
          {limit_loaded && groups.length === 0 ? (
            <div className="gm-empty">
              <span className="icon d-block mb-2">{""}</span>
              {limit_rows.length ? "No company matches the selected filters." : "No company has been set up yet. Add companies in Center Config › Config Company."}
            </div>
          ) : (
            <div className="row g-3">{groups.map((c) => this.renderLimitCompany(c, period))}</div>
          )}
        </div>
      </div>
    );
  }
}
